import axios from 'axios'
import type { AxiosInstance } from 'axios'
import { TOKEN_KEYS } from '@/types/auth'

const configuredApiUrl = import.meta.env.VITE_API_URL?.trim()

if (!configuredApiUrl && import.meta.env.PROD) {
  throw new Error('VITE_API_URL is required for production builds')
}

export const API_BASE_URL = (configuredApiUrl || 'http://localhost:3002/api').replace(/\/$/, '')

export const getAuthToken = (): string | null => {
  const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true'
  const storage = rememberMe ? localStorage : sessionStorage
  return storage.getItem(TOKEN_KEYS.ACCESS_TOKEN)
}

export const clearAuthStorage = () => {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN)
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(TOKEN_KEYS.REMEMBER_ME)
  localStorage.removeItem(TOKEN_KEYS.USER_DATA)

  sessionStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN)
  sessionStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN)
  sessionStorage.removeItem(TOKEN_KEYS.REMEMBER_ME)
  sessionStorage.removeItem(TOKEN_KEYS.USER_DATA)
}

export const handleUnauthorized = () => {
  clearAuthStorage()
  window.location.href = '/'
}

export const extractApiErrorMessage = (error: any, fallback = 'An unexpected error occurred') => {
  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  if (error?.message) {
    return error.message
  }

  return fallback
}

export const createAuthenticatedApi = (baseURL: string = API_BASE_URL): AxiosInstance => {
  const api = axios.create({
    baseURL,
    timeout: 15000
  })

  api.interceptors.request.use((config) => {
    const token = getAuthToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  })

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized()
      }

      return Promise.reject(error)
    }
  )

  return api
}

export const createPublicApi = (baseURL: string = API_BASE_URL): AxiosInstance => {
  return axios.create({
    baseURL,
    timeout: 15000
  })
}
